import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { FC, useMemo, useState } from 'react';
import {
  Control,
  useController,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';

import CurrencySymbol from '~/components/common/CurrencySymbol';
import type { ContractFormValues } from '~/components/contracts/types';
import { ContractForm } from '~/components/contracts/types';
import { LAISelect, LAITextField } from '~/components/form';
import { FieldAndCell, FieldDef } from '~/components/form/utils';
import type { Car } from '~/data/generated/model';
import { ContractTypeOptions } from '~/data/schemas/contract';
import { usePropertyCars } from '~/hooks/usePropertyCars';
import { useProviders } from '~/hooks/useProviders';
import { BUTTON_SX } from '~/theme';

type ServiceProviderFieldProps = {
  control: Control<ContractFormValues>;
  propertyId: string;
  action: ContractForm['action'];
};

const ServiceProviderField = ({ control, propertyId, action }: ServiceProviderFieldProps) => {
  const { field } = useController({
    name: 'serviceProvider',
    control,
  });
  const { providers, loading } = useProviders({ propertyId });
  const { errors } = useFormState({ control });

  const options = useMemo(() => {
    return (
      providers
        ?.map((provider) => ({
          label: provider.name,
          value: provider.id,
        }))
        .concat([{ label: 'None', value: '' }]) ?? []
    );
  }, [providers]);

  return (
    <LAISelect
      hookFormRef={field.ref}
      name={field.name}
      readOnly={action === 'update'}
      label={loading ? 'Loading...' : 'Service Provider *'}
      fullWidth
      options={options}
      value={field.value}
      onChange={field.onChange}
      onBlur={() => field.onBlur()}
      disabled={field.disabled}
      helperText={errors.serviceProvider?.message}
      error={!!errors.serviceProvider}
    />
  );
};

type GetContractDetailsFieldsProps = {
  action: ContractForm['action'];
  control: Control<ContractFormValues>;
  propertyId: string;
};

const getContractDetailsFields = ({
  action,
  control,
  propertyId,
}: GetContractDetailsFieldsProps) => {
  const contractDetailsFields: FieldDef[] = [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'serviceProvider',
      type: 'custom',
      element: <ServiceProviderField control={control} propertyId={propertyId} action={action} />,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'startDate',
      label: 'Effective Since',
      type: 'date',
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'expirationDate',
      label: 'Expiration',
      type: 'date',
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    { name: 'term', label: 'Term', type: 'text', gridProps: { xs: 12, sm: 6, md: 4 } },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      options: Object.entries(ContractTypeOptions).map(([value, label]) => ({ id: value, label })),
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'contractPaper',
      label: 'Contract Paper',
      type: 'text',
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'cancellation',
      label: 'Cancellation (days)',
      type: 'text',
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'hourlyFeeAdjustmentRate',
      label: 'Adjustment Rate (yearly)',
      type: 'number',
      gridProps: { xs: 12, sm: 6, md: 4 },
      fieldProps: {
        slotProps: {
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
          htmlInput: {
            min: 0,
          },
        },
      },
    },
    {
      name: 'partsAndMaterialsMarkup',
      label: 'Parts and Materials Markup',
      type: 'number',
      gridProps: { xs: 12, sm: 6, md: 4 },
      fieldProps: {
        slotProps: {
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
          htmlInput: {
            min: 0,
          },
        },
      },
    },
    {
      name: 'businessHours',
      label: 'Regular Business Hours',
      type: 'text',
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'phoneMonitoring',
      label: 'Phone Monitoring',
      type: 'select',
      options: [
        { id: 'unknown', label: 'Unknown' },
        { id: 'true', label: 'Yes' },
        { id: 'false', label: 'No' },
      ],
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'fireService',
      label: 'Fire Service',
      type: 'select',
      options: [
        { id: 'unknown', label: 'Unknown' },
        { id: 'true', label: 'Yes' },
        { id: 'false', label: 'No' },
      ],
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'pressureTest',
      label: 'Pressure Test',
      type: 'select',
      options: [
        { id: 'unknown', label: 'Unknown' },
        { id: 'true', label: 'Yes' },
        { id: 'false', label: 'No' },
      ],
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'text-multiline',
      required: false,
      gridProps: { xs: 12 },
    },
  ];

  return contractDetailsFields;
};

type CarsCoveredByContractProps = {
  propertyId: string;
  control: Control<ContractFormValues>;
};

const getDefaultCarKpiTargets = (
  car: Pick<Car, 'id' | 'name' | 'ahjId' | 'elevatorType'>,
): ContractForm['propertyContract']['cars'][number] => {
  return {
    name: car.name,
    carId: car.id,
    ahjId: car.ahjId ?? '',
    elevatorType: car.elevatorType ?? '',
    maintenanceQuantity: 1,
    maintenanceUnit: 'visits',
    maintenanceTimePeriod: 1,
    regularCallbackResponseTime: 60,
    emergencyCallbackResponseTime: 15,
    regularCallbackRate: 4,
    emergencyCallbackRate: 3,
  };
};

const CarsCoveredByContract = ({ propertyId, control }: CarsCoveredByContractProps) => {
  const { cars, loading } = usePropertyCars(propertyId);
  const { field } = useController({ name: 'propertyContract.cars', control });
  const [selectedCarIds, setSelectedCarIds] = useState<string[]>([]);

  const carsOptions = useMemo(() => {
    return (
      cars?.map((car) => ({
        label: car.name,
        value: car.id,
      })) ?? []
    );
  }, [cars]);

  const carsByIdMap = useMemo(() => {
    return new Map(cars?.map((car) => [car.id, car]));
  }, [cars]);

  const onChange = useMemo(() => {
    return (value: string[]) => {
      setSelectedCarIds(value);
      const selectedCars = value.map((id) => {
        const car = carsByIdMap.get(id);
        if (!car) {
          return null;
        }
        return getDefaultCarKpiTargets(car);
      });
      field.onChange(selectedCars);
    };
  }, [field, carsByIdMap]);

  return (
    <LAISelect
      hookFormRef={field.ref}
      label={loading ? 'Loading...' : 'Cars covered by this contract'}
      name={field.name}
      fullWidth
      multiple
      options={carsOptions}
      onChange={onChange}
      onBlur={() => field.onBlur()}
      disabled={field.disabled}
      value={selectedCarIds}
    />
  );
};

type RateSetsSelectProps = {
  control: Control<ContractFormValues>;
  rateSets: ContractFormValues['rateSets'];
};

const RateSetsSelect = ({ control, rateSets }: RateSetsSelectProps) => {
  const { field } = useController({ name: 'propertyContract.rateSetId', control });
  const { errors } = useFormState({ control });

  const hasRateSets = rateSets.length > 0;

  const options = useMemo(() => {
    const rateSetOptions = rateSets.map((rateSet) => ({
      label: rateSet.name,
      value: rateSet.id ?? '',
    }));
    // Add "None" option to allow contracts without rate sets
    return [{ label: 'None', value: '' }, ...rateSetOptions];
  }, [rateSets]);

  return (
    <LAISelect
      label="Rate Set for this Property (Optional)"
      hookFormRef={field.ref}
      name={field.name}
      value={field.value ?? ''}
      fullWidth
      options={options}
      onChange={(value) => {
        field.onChange(value === '' ? null : value);
      }}
      onBlur={() => field.onBlur()}
      disabled={field.disabled}
      helperText={
        hasRateSets
          ? errors.propertyContract?.rateSetId?.message
          : 'No rate sets available. You can add one in the Rate Sets section.'
      }
      error={!!errors.propertyContract?.rateSetId}
    />
  );
};

const ContractDetailsFields: FC = () => {
  const { control } = useFormContext<ContractFormValues>();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'customDetails',
  });
  const [customLabel, setCustomLabel] = useState('');

  const theme = useTheme();

  const action = useWatch({ control, name: 'action' });
  const propertyId = useWatch({ control, name: 'propertyContract.propertyId' });

  const contractDetailsFields = useMemo(() => {
    return getContractDetailsFields({ action, control, propertyId });
  }, [action, control, propertyId]);

  return (
    <>
      <Grid container spacing={2} my={2}>
        {contractDetailsFields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
        {fields.map((field, index) => (
          <Grid container key={field.id} {...{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <LAITextField
                name={`customDetails.${index}.value`}
                type="text"
                control={control}
                label={field.name}
                fullWidth
                size="small"
                slotProps={{
                  htmlInput: {
                    role: 'textbox',
                  },
                }}
              />
              <IconButton onClick={() => remove(index)}>
                <Delete />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid
        sx={{
          display: 'flex',
          width: '295px',
          justifyContent: 'center',
          flexDirection: 'column',
          backgroundColor: theme.palette.primary.light,
          padding: '16px',
          gap: '8px',
          borderRadius: '8px',
        }}
      >
        <Grid>
          <Typography
            variant="h6"
            sx={{ fontSize: '14px', lineHeight: '157%', letterSpacing: '0.1px' }}
          >
            Add your own Summary Item
          </Typography>
        </Grid>
        <Grid
          container
          sx={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-start',
            flexDirection: 'row',
            alignSelf: 'stretch',
          }}
        >
          <TextField
            name="addCustomItem"
            type="text"
            label="Item Label"
            size="small"
            sx={{ width: '170px', backgroundColor: '#fff', borderRadius: '4px' }}
            slotProps={{
              htmlInput: {
                role: 'textbox',
              },
            }}
            value={customLabel}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCustomLabel(event.target.value);
            }}
          />
          <Button
            type="button"
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              if (!customLabel) {
                return;
              }

              append({
                name: customLabel,
                label: customLabel,
                value: '',
              });
              setCustomLabel('');
            }}
            sx={BUTTON_SX}
          >
            ADD
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export const ContractGeneralSectionForm = () => {
  const hookForm = useFormContext<ContractFormValues>();
  const { control } = hookForm;

  const rateSets = useWatch({ control, name: 'rateSets' });
  const action = useWatch({ control, name: 'action' });
  const propertyId = useWatch({ control, name: 'propertyContract.propertyId' });
  // Subscribed, not read through `getValues`: the heading below renders the
  // contract name, and `getValues` does not re-render when the field changes.
  const contractName = useWatch({ control, name: 'name' });

  return (
    <>
      {/* Property Contract Details Section */}
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {contractName ?? 'Property'} Contract
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <LAITextField
            label="Effective Since"
            type="date"
            fullWidth
            size="small"
            name="propertyContract.startDate"
            control={hookForm.control}
            slotProps={{
              input: {
                inputProps: {
                  max: new Date().toISOString().split('T')[0], // Prevents selecting future dates
                },
              },
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <LAITextField
            label="Expiration"
            type="date"
            fullWidth
            size="small"
            name="propertyContract.expirationDate"
            control={hookForm.control}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <LAITextField
            label="Monthly Pricing"
            type="number"
            fullWidth
            size="small"
            name="propertyContract.monthlyPrice"
            control={hookForm.control}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <CurrencySymbol />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>

      {action === 'create' ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <CarsCoveredByContract propertyId={propertyId} control={hookForm.control} />
          </Grid>
        </Grid>
      ) : null}

      {/* Rate Sets Selection */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <RateSetsSelect control={hookForm.control} rateSets={rateSets} />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Contract Summary
      </Typography>

      <ContractDetailsFields />
    </>
  );
};
